import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { IPageHeadline } from '../../../shared/classes/page-title-parameters';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { ILargeCardInteriorOptions } from '../../../shared/interfaces/card-data';
import { DisplayOptions } from '../../../shared/interfaces/display-options';
import { NoDataActionType } from '../../../shared/interfaces/no-data';
import {
    IHeadcountTrendsData,
    IHistoricalDataParameters,
} from '../../interfaces/historical-trends';
import { HistoricalTrendsService } from '../../services/historical-trends.service';
import * as Highcharts from 'highcharts';
import { HeadcountTrendsChartOptions } from '../../../shared/classes/headcount-trends-chart-options';
import { CustomDashboardsOutletDirective } from 'app/modules/my-limegear/my-limegear-outlet.directive';
import { CustomDashboardsService } from 'app/modules/my-limegear/services/my-limegear.service';
import { ComponentsOutletDirectiveService } from 'app/modules/my-limegear/services/component-outlet-directive.service';

interface ISeriesData {
    name: string;
    data: Array<any>;
    type: string;
    color?: string;
    marker?: object;
}

interface IDataPoint {
    y: number;
    name: string;
    properties: object;
}

@Component({
    selector: 'headcount-trends',
    templateUrl: './headcount-trends.component.html',
    styleUrls: ['./headcount-trends.component.scss'],
})
export class HeadcountTrendsComponent implements OnInit {
    constructor(
        private historicalTrendsService: HistoricalTrendsService,
        private _router: Router,
        private _reusableFunctions: ReusableFunctions,
        private customDashboardsService: CustomDashboardsService,
        private componentsOutletDirectiveService: ComponentsOutletDirectiveService,
        private cd: ChangeDetectorRef
    ) {}

    @ViewChildren(CustomDashboardsOutletDirective)
    dashboardOutlet: QueryList<CustomDashboardsOutletDirective>;

    hFilterJSON = '';
    displayOptions: DisplayOptions[];
    pageHeadline: IPageHeadline;
    action: NoDataActionType;
    largeCardInteriorOptions: ILargeCardInteriorOptions;
    parameters: IHistoricalDataParameters;
    headcountTrendsChartOptions: HeadcountTrendsChartOptions;
    dashboard = 'historical-trends';
    largeCardIdArray: Array<string>;
    pageTitle: string = 'Headcount Trends';

    ngOnInit() {
        this.setPagetitle();
        this.customDashboardsService.destroyCharts();
        this.customDashboardsService.detectChange(
            '',
            [],
            false,
            false,
            NoDataActionType.pageStart,
            this.dashboard
        );

        this.largeCardIdArray = ['historical-trends-headcount-trends'];
        this.updateComponent();
        this.action = NoDataActionType.pageStart;

        this.setPagetitle();
        // this.initializeLargeCard();
        // this.prepareData();
    }

    setPagetitle() {
        this.pageHeadline = {
            module: 'historical-trends',
            moduleIcon: 'fa-chart-line',
            pageTitle: 'Headcount Trends',
            linkArray: [
                {
                    moduleLink: 'historical-trends',
                    linkTitle: 'Historical Trends Home',
                    showLink: true,
                    isHome: true,
                },
                {
                    moduleLink: 'historical-trends/insights',
                    linkTitle: 'Historical Trends Insights',
                    showLink: true,
                },
            ],
        };
    }

    setFilterOptions() {
        if (this.displayOptions?.length > 0) {
            this.parameters.fromDatePeriod = this.displayOptions.find(
                (x) => x.optionKey == 'dateRangeRolling_to_36m'
            ).selectedOptions;
            this.parameters.asOf = this.displayOptions.find(
                (x) => x.optionKey == 'scorecardAsOf'
            ).selectedOptions;
            this.parameters.positionAbbv = this.displayOptions.find(
                (x) => x.optionKey == 'groupByPosition_multiSelect'
            ).selectedOptions;
        }
        this.parameters.hFilterJSON = this.hFilterJSON;
    }

    initializeLargeCard() {
        this.largeCardInteriorOptions = {
            action: this.action,
            componentTitle: this.pageTitle,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            accessToUrl: '/historical-trends/insights/headcount-trends',
            uniqueId: 'historical-headcount-trends',
        };
    }

    prepareData() {
        this.setFilterOptions();
        let largeCardInteriorOptions = this.largeCardInteriorOptions;
        this.largeCardInteriorOptions.action = this.action;
        this.largeCardInteriorOptions.hasData = false;
        this.largeCardInteriorOptions.isDataReady = false;

        this.historicalTrendsService.geHeadcountData(this.parameters).subscribe(
            (res: IHeadcountTrendsData[]) => {
                if (res?.length > 0) {
                    let positions = this._reusableFunctions.getUniqueItemList(res, 'position');
                    let allSeriesData: ISeriesData[] = [];

                    positions.forEach((pos) => {
                        let seriesData: ISeriesData = {
                            name: pos,
                            type: 'spline',
                            data: [],
                        };

                        let positionDataSet = res.filter((item) => item.position === pos);
                        positionDataSet.forEach((point) => {
                            let pointData: IDataPoint = {
                                y: point.employeeCount,
                                name: point.yearMonthCalendar,
                                properties: {
                                    positionAbbv: point.positionAbbv,
                                    position: point.position,
                                },
                            };

                            seriesData.data.push(pointData);
                        });
                        allSeriesData.push(seriesData);
                    });

                    let chartOptions =
                        this.headcountTrendsChartOptions.createNewHistoricalChartOptions(
                            null,
                            'Headcount',
                            allSeriesData,
                            '/reporting/historical-trends/insights_headcount-trends',
                            true,
                            null
                        );

                    largeCardInteriorOptions.hasData = res.length > 0;
                    largeCardInteriorOptions.isDataReady = true;
                    largeCardInteriorOptions.chart = new Highcharts.Chart(
                        largeCardInteriorOptions.uniqueId,
                        chartOptions
                    );
                } else {
                    largeCardInteriorOptions.hasData = false;
                    largeCardInteriorOptions.isDataReady = true;
                }
                this.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
            },
            (err) => {
                largeCardInteriorOptions.hasData = false;
                largeCardInteriorOptions.isDataReady = true;
                largeCardInteriorOptions.action = NoDataActionType.error;
                this.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
            }
        );
    }

    applyNewFilters(event: string) {
        this.customDashboardsService.destroyCharts();
        this.hFilterJSON = event;
        this.updateComponent();
        this.customDashboardsService.detectChange(
            this.hFilterJSON,
            this.displayOptions,
            true,
            false,
            NoDataActionType.filter,
            this.dashboard
        );
    }

    updateComponent() {
        this.componentsOutletDirectiveService.detectViewChanges(this.cd);
        this.componentsOutletDirectiveService.loadContents(this.dashboardOutlet);
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.customDashboardsService.destroyCharts();
        this.displayOptions = event;
        this.updateComponent();
        this.customDashboardsService.detectChange(
            this.hFilterJSON,
            this.displayOptions,
            false,
            true,
            NoDataActionType.filter,
            this.dashboard
        );
    }

    ngOnDestroy() {
        this.customDashboardsService.unsubscribeFilters();
        this.customDashboardsService.destroyCharts();
    }
}
